import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Button, Label, Input, Table, Spinner } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import Header from "../../../layout/Header/Header";

/**
 * Devolucion de material al proveedor, por CANTIDAD.
 *
 * Se elige la compra y de cada material que trajo se pone cuanto se devuelve
 * (tope: lo comprado menos lo ya devuelto). Antes la tela se devolvia
 * escaneando el QR de cada rollo; ahora tela y habilitacion funcionan igual.
 * Sale del inventario por almacen y respeta lo apartado para pedidos.
 */
function DevolucionesProveedoresCreate({ tipo = "Tela" }) {
  const { user } = useContext(AuthContext);
  const URL_COMPRAS = process.env.REACT_APP_URL_COMPRAS;
  const URL_AREAS = process.env.REACT_APP_URL_AREAS;
  const URL_INVENTARIOS_MATERIALES = process.env.REACT_APP_URL_INVENTARIOS_MATERIALES;
  const URL_DEVOLUCION_MATERIALES = process.env.REACT_APP_URL_DEVOLUCION_MATERIALES;

  const [compras, setCompras] = useState([]);
  const [compra, setCompra] = useState("");
  const [detalle, setDetalle] = useState(null);
  const [cantidades, setCantidades] = useState({});
  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState("");
  const [existencias, setExistencias] = useState([]);
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [observaciones, setObservaciones] = useState("");
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cab = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });

  useEffect(() => {
    axios.get(URL_COMPRAS, cab())
      .then((r) => setCompras(r.data.filter((c) => c.ordenesCompra?.[0]?.tipo === tipo).sort((a, b) => b.idCompras - a.idCompras)))
      .catch((e) => console.log(e));
    axios.get(URL_AREAS, cab()).then((r) => {
      const bodegas = r.data.filter((a) => (a.tipo || "Bodega") === "Bodega");
      setAreas(bodegas);
      const principal = bodegas.find((a) => a.name === "Almacen") || bodegas[0];
      if (principal) setArea(principal._id);
    });
  }, [tipo]);

  useEffect(() => {
    if (!area) return;
    let vigente = true; // si cambian de almacen, la respuesta anterior ya no vale
    axios.get(`${URL_INVENTARIOS_MATERIALES}?areas=${area}`, cab()).then((r) => vigente && setExistencias(r.data)).catch((e) => console.log(e));
    return () => { vigente = false; };
  }, [area]);

  function eligeCompra(id) {
    setCompra(id);
    setDetalle(null);
    setCantidades({});
    if (!id) return;
    setCargando(true);
    axios.get(`${URL_DEVOLUCION_MATERIALES}/compra/${id}`, cab())
      .then((r) => setDetalle(r.data))
      .catch((e) => Swal.fire({ icon: "error", title: "No se pudo leer la compra", text: e.response?.data?.message || e.message }))
      .finally(() => setCargando(false));
  }

  const idDe = (v) => (v && v._id ? v._id : v || "");
  const enBodega = (r) => {
    const s = existencias.find((e) => e.tipoMaterial === r.tipoMaterial && idDe(e.material) === r.material &&
      (r.tipoMaterial === "Telas" ? idDe(e.colores) === (r.colores || "") : true));
    return s ? s.disponible ?? s.cantidad : 0;
  };
  const clave = (r) => `${r.material}|${r.colores || ""}`;
  const maximo = (r) => Math.max(0, Math.min(r.devolvible, enBodega(r)));

  const renglones = detalle ? detalle.renglones : [];
  const elegidos = renglones.filter((r) => (parseFloat(cantidades[clave(r)]) || 0) > 0);
  const subTotal = elegidos.reduce((t, r) => t + parseFloat(cantidades[clave(r)]) * r.costo, 0);
  const iva = detalle && detalle.compra.conIva ? subTotal * 0.16 : 0;
  const total = subTotal + iva;
  const fmt = (n) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  async function guardar() {
    if (!elegidos.length) return Swal.fire({ icon: "warning", title: "Pon cuanto devuelves de al menos un material" });
    const excedido = elegidos.find((r) => parseFloat(cantidades[clave(r)]) > maximo(r));
    if (excedido) {
      return Swal.fire({ icon: "error", title: "Cantidad de mas", text: `${excedido.nombre} ${excedido.color}: maximo ${maximo(excedido)}` });
    }
    setGuardando(true);
    try {
      const { data } = await axios.post(URL_DEVOLUCION_MATERIALES, {
        compras: compra, fecha, observaciones, areas: area,
        subTotal: parseFloat(subTotal.toFixed(2)), iva: parseFloat(iva.toFixed(2)), total_general: parseFloat(total.toFixed(2)),
        renglones: elegidos.map((r) => ({
          tipoMaterial: r.tipoMaterial, material: r.material, colores: r.colores || undefined,
          cantidad: parseFloat(cantidades[clave(r)]), costo: r.costo,
        })),
      }, cab());
      await Swal.fire({ icon: "success", title: `Devolucion ${data.idDevolucionProveedor} registrada`, text: `$${fmt(total)}` });
      window.location.href = "/ListadoDevolucionesProveedores";
    } catch (error) {
      const info = error.response?.data || {};
      Swal.fire({ icon: "error", title: "No se pudo registrar", text: info.message || error.message });
      setGuardando(false);
    }
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.devolucionesProveedores_create ? (
        <div className="card container col-10">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <i className="fas fa-undo" style={{ marginRight: 8, color: "#f59e0b" }} />
                Devolucion de {tipo === "Tela" ? "Tela" : "Habilitacion"} a Proveedor
              </h3>
              <p className="page-subtitle">Elige la compra y cuanto devuelves de cada material. Sin rollos ni QR.</p>
            </div>
          </div>

          <Row>
            <Col md={4}>
              <Label>Compra</Label>
              <Input type="select" value={compra} onChange={(e) => eligeCompra(e.target.value)}>
                <option value="">Selecciona una compra</option>
                {compras.map((c) => (
                  <option value={c._id} key={c._id}>
                    # {c.idCompras} / {c.ordenesCompra?.[0]?.proveedores?.[0]?.nombre_comercial ?? ""} / Factura {c.factura_proveedor}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={2}>
              <Label>Fecha</Label>
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </Col>
            <Col md={2}>
              <Label>Sale de</Label>
              <Input type="select" value={area} onChange={(e) => setArea(e.target.value)}>
                {areas.map((a) => (
                  <option value={a._id} key={a._id}>{a.name}</option>
                ))}
              </Input>
            </Col>
            <Col md={4}>
              <Label>Observaciones</Label>
              <Input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Motivo de la devolucion" />
            </Col>
          </Row>
          <br />

          {cargando ? <Spinner /> : undefined}
          {detalle ? (
            <>
              <p className="text-muted">
                Compra {detalle.compra.idCompras} · {detalle.compra.proveedor} · Factura {detalle.compra.factura_proveedor}
              </p>
              <Table size="sm" striped borderless responsive>
                <thead>
                  <tr>
                    <th>Material</th>
                    <th>Color</th>
                    <th style={{ textAlign: "right" }}>Comprado</th>
                    <th style={{ textAlign: "right" }}>Ya devuelto</th>
                    <th style={{ textAlign: "right" }}>En bodega</th>
                    <th style={{ textAlign: "right" }}>Costo</th>
                    <th style={{ width: 130 }}>Devolver</th>
                    <th style={{ textAlign: "right" }}>Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {renglones.map((r) => {
                    const n = parseFloat(cantidades[clave(r)]) || 0;
                    const excede = n > maximo(r);
                    return (
                      <tr key={clave(r)}>
                        <td>{[r.descripcion, r.nombre].filter(Boolean).join(" / ")}</td>
                        <td>{r.color}</td>
                        <td style={{ textAlign: "right" }}>{r.comprado} {r.unidad}</td>
                        <td style={{ textAlign: "right" }}>{r.devuelto}</td>
                        <td style={{ textAlign: "right" }}>{enBodega(r)}</td>
                        <td style={{ textAlign: "right" }}>${fmt(r.costo)}</td>
                        <td>
                          <Input
                            bsSize="sm"
                            type="number"
                            min={0}
                            max={maximo(r)}
                            disabled={maximo(r) <= 0}
                            value={cantidades[clave(r)] ?? ""}
                            onChange={(e) => setCantidades({ ...cantidades, [clave(r)]: e.target.value })}
                            style={excede ? { borderColor: "#dc2626" } : undefined}
                          />
                        </td>
                        <td style={{ textAlign: "right" }}>${fmt(n * r.costo)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <h5 style={{ textAlign: "right" }}>
                Subtotal ${fmt(subTotal)} {iva ? `· IVA $${fmt(iva)}` : ""} · Total ${fmt(total)}
              </h5>
            </>
          ) : undefined}

          <div className="submit-bar">
            <Button href="/ListadoDevolucionesProveedores" className="btn btn-danger">Regresar</Button>
            <Button className="btn btn-success" onClick={guardar} disabled={guardando || !detalle}>
              <i className="fas fa-save" style={{ marginRight: 6 }} /> Guardar
            </Button>
          </div>
          <br />
        </div>
      ) : undefined}
    </>
  );
}

export default DevolucionesProveedoresCreate;
